import { useState } from "react";
import {
  Container,
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  TextField,
  IconButton,
  TableHead,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { useLang } from "../context/LangContext";
import locales from "../locales.json";
import { CheckCircle, RemoveCircle, HourglassEmpty } from "@mui/icons-material";
import { useSubscriptionInfo } from "../hooks/useSubscriptionInfo";
import InvoiceTable from "./InvoiceTable";

function Account() {
  const [newEmail, setNewEmail] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const navigate = useNavigate();
  const { lang } = useLang();
  const { account_page } = locales;

  const {
    accountType,
    renewalDate,
    cancellationDate,
    licenses,
    numberOfLicenses,
    subscriptionToken,
    invoices,
    fetchSubscriptionInfo,
  } = useSubscriptionInfo();

  const handleSendInvitation = async (email) => {
    try {
      const response = await fetchWithAuth("/authentication/send-invitation", {
        method: "POST",
        body: JSON.stringify({
          recipientEmail: email,
          subscriptionToken,
          lang,
        }),
      });

      //alert(account_page.invitation_sent);

      const data = await response.json();
      if (data.error) {
        alert(data.message);
      } else {
        alert(account_page.invitation_sent[lang]);
      }
      fetchSubscriptionInfo();
    } catch (error) {
      console.error("Erreur lors de l'envoi de l'invitation:", error);
    }
  };

  const handleDissociate = async (email) => {
    try {
      await fetchWithAuth("/authentication/dissociate", {
        method: "POST",
        body: JSON.stringify({ subscriptionToken, email }),
      });
      alert(account_page.license_dissociated[lang]);
      fetchSubscriptionInfo();
    } catch (error) {
      console.error("Erreur lors de la dissociation de la licence:", error);
    }
  };

  const handleDowngradeAccount = async () => {
    const confirmCancel = window.confirm(account_page.confirm_cancel[lang]);

    if (!confirmCancel) return;

    try {
      const response = await fetchWithAuth(`/subscriptions/unsubscribe`, {
        method: "POST",
      });

      const data = await response.json();

      if (response.ok) {
        alert(account_page.subscription_canceled[lang]);
        fetchSubscriptionInfo(); // Rafraîchit les infos après annulation
      } else {
        alert(data.error || account_page.error_cancel[lang]);
      }
    } catch (error) {
      console.error(account_page.cancel_error[lang], error);
      alert(account_page.retry_error[lang]);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      const response = await fetchWithAuth("/users/me", {
        method: "DELETE",
      });

      if (response.ok) {
        alert(account_page.delete_account_success[lang]);
        localStorage.removeItem("auth_token");
        navigate("/signin");
      } else {
        const data = await response.json();
        alert(data.error || account_page.delete_account_error[lang]);
      }
    } catch (error) {
      console.error("Error deleting account:", error);
      alert(account_page.delete_account_error[lang]);
    } finally {
      setDeleteDialogOpen(false);
      setDeleteConfirmText("");
    }
  };

  return (
    <Container
      maxWidth="sm"
      sx={{
        display: "flex",
        alignItems: "center", // Centre horizontalement
        justifyContent: "flex-start", // Commence en haut
        py: { xs: 3, md: 4 }, // Padding vertical léger
        px: { xs: 1, sm: 2, md: 0 },
        boxSizing: "border-box",
        width: "100%",
      }}
    >
      <Box
        sx={{
          backgroundColor: "#f5f5f5",
          padding: { xs: "16px", sm: "24px", md: "32px" },
          borderRadius: "16px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          width: "100%",
          maxWidth: "600px",
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h4"
          gutterBottom
          sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, textAlign: "center" }}
        >
          {account_page.title[lang]}
        </Typography>

        <TableContainer
          component={Paper}
          sx={{ marginY: "24px", width: "100%", overflowX: "auto" }}
        >
          <Table>
            <TableBody>
              <TableRow>
                <TableCell>
                  <Typography variant="subtitle1">
                    {account_page.account_type[lang]}
                  </Typography>
                </TableCell>
                <TableCell>
                  {accountType === null
                    ? account_page.loading[lang]
                    : account_page[accountType][lang]}
                </TableCell>
              </TableRow>
              <TableRow>
                {cancellationDate ? (
                  <>
                    <TableCell>
                      <Typography variant="subtitle1">
                        {account_page.cancellation_date[lang]}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {cancellationDate || account_page.loading[lang]}
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell>
                      <Typography variant="subtitle1">
                        {account_page.renewal_date[lang]}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {renewalDate || account_page.loading[lang]}
                    </TableCell>
                  </>
                )}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {accountType === "enterprise" && (
          <Box>
            <Typography
              variant="h6"
              sx={{ fontSize: { xs: "1.1rem", md: "1.25rem" } }}
            >
              {`${account_page.license_management[lang]} (${numberOfLicenses})`}
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{ fontSize: { xs: "1rem", md: "1.1rem" } }}
            >
              {`(${numberOfLicenses - licenses.length} ${account_page.license_available[lang]})`}
            </Typography>
            {/* Ajout d'un nouvel email */}
            <Box
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              alignItems="center"
              gap={2}
              mt={2}
            >
              <TextField
                label={account_page.add_email_placeholder[lang]}
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                fullWidth
              />
              <Button
                variant="contained"
                onClick={() => handleSendInvitation(newEmail)}
                sx={{ minWidth: { xs: "100%", sm: "120px" } }}
              >
                {account_page.send_invitation_button[lang]}
              </Button>
            </Box>

            {/* Tableau récapitulatif des licences */}
            <TableContainer
              component={Paper}
              sx={{ marginY: "24px", width: "100%", overflowX: "auto" }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>{account_page.license_email[lang]}</TableCell>
                    <TableCell>{account_page.license_status[lang]}</TableCell>
                    <TableCell>{account_page.license_remove[lang]}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {licenses.map((license, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Typography variant="body1" color="textSecondary">
                          {index + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" color="textSecondary">
                          {license.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {license.status === "active" ? (
                          <CheckCircle color="success" />
                        ) : license.status === "pending" ? (
                          <HourglassEmpty color="warning" />
                        ) : (
                          <Typography color="error">
                            {account_page.license_status_pending[lang]}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={() => handleDissociate(license.email)}
                        >
                          <RemoveCircle color="error" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              justifyContent="center"
              gap={2}
              mt={3}
              sx={{ marginY: "24px" }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate("/license-adjustment")}
              >
                {account_page.license_adjustment[lang]}
              </Button>
            </Box>
          </Box>
        )}

        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent="center"
          gap={2}
          mt={2}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate("/")}
          >
            {account_page.home_button[lang]}
          </Button>
          {accountType !== "free" && !cancellationDate ? (
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleDowngradeAccount}
            >
              {account_page.cancel_button[lang]}
            </Button>
          ) : (
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate("/payment")}
            >
              {account_page.upgrade_button[lang]}
            </Button>
          )}
          <Button
            variant="outlined"
            color="error"
            onClick={() => setDeleteDialogOpen(true)}
          >
            {account_page.delete_account_button[lang]}
          </Button>
        </Box>

        {(accountType === "enterprise" || accountType === "premium") && (
          <InvoiceTable invoices={invoices} />
        )}
      </Box>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setDeleteConfirmText("");
        }}
      >
        <DialogTitle>
          {account_page.delete_account_dialog_title[lang]}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {account_page.delete_account_dialog_body[lang]}
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder="DELETE"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setDeleteConfirmText("");
            }}
          >
            {{ fr: "Annuler", de: "Abbrechen", it: "Annulla", en: "Cancel" }[
              lang
            ] ?? "Cancel"}
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={deleteConfirmText !== "DELETE"}
            onClick={handleDeleteAccount}
          >
            {account_page.delete_account_confirm_button[lang]}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Account;
